// All GraphQL documents plus hand-written result types. Field names follow
// pdbq's PostGraphile-style conventions (camelCase columns, `<child>By<Fk>`
// reverse relations, UPPERCASE enums, computed columns with the table prefix
// stripped: route_start_point → startPoint).
import { gql } from "./gql";
import type {
  Activity,
  ClimbStyle,
  ClubJoinPolicy,
  ClubMemberRole,
  ClubMemberStatus,
  ClubVisibility,
  ConditionRating,
  EventJoinPolicy,
  EventParticipantRole,
  EventParticipantStatus,
  EventRecurrence,
  EventVisibility,
  OutingStatus,
  OutingVisibility,
  PlaceType,
  TickPriority,
} from "./format";
import type { EntityI18n } from "./i18n";
import type { GeoPoint, Geometry } from "./geo";

// `i18n` is the jsonb translations column ({"name": {"de": …, "fr": …}}).
// Optional: detail pages and search select it; the map viewport documents
// deliberately do NOT (the payload cost per feature isn't worth it for labels).
export type PlacePin = {
  id: string;
  name: string;
  i18n?: EntityI18n;
  type: PlaceType;
  elevation: number | null;
  geom: GeoPoint;
};

export type RoutePin = {
  id: string;
  name: string;
  i18n?: EntityI18n;
  activities: Activity[];
  durationMin: number | null;
  startPoint: GeoPoint | null;
  mainGeom?: Geometry | null;
};

export type RegionShape = {
  id: string;
  name: string;
  i18n?: EntityI18n;
  type: string;
  geomSimple: Geometry | null;
};

export type OutingPin = {
  id: string;
  title: string;
  activity: Activity;
  status: OutingStatus;
  date: string;
  rating: number | null;
  point: GeoPoint | null;
};

// Map data comes from the generated Relay connections, not the map_* SQL
// functions: pdbq ≥ 0.8.0 exposes PostGIS predicates as `GeometryFilterOps`,
// so `geom: { bboxIntersects: $bbox }` replaces the functions' hardcoded
// `&& ST_MakeEnvelope`. That buys real pagination (totalCount + pageInfo) and
// lifts the functions' silent LIMIT caps (routes capped at 1000 against a
// 13.5k-row viewport). Every filter that used to be sieved client-side is now
// pushed into the filter argument.
//
// Each layer is its own document so pages stream in independently and one
// slow layer never blocks another. `$offset` drives parallel page fetching;
// ordering is by id so paging stays stable.
export type PageInfo = { hasNextPage: boolean; endCursor: string | null };
// totalCount is behind @include(if: $withTotal): the count(*) subquery re-runs
// the whole bbox filter and dominates wide-viewport pages (~200ms measured), so
// only the first page of a fan-out pays for it.
export type Connection<T> = { totalCount?: number; pageInfo: PageInfo; nodes: T[] };

export const MAP_PLACES_PAGE = gql`
  query MapPlacesPage($filter: PlaceFilter, $first: Int!, $offset: Int!, $withTotal: Boolean!) {
    places(first: $first, offset: $offset, orderBy: [ID_ASC], filter: $filter) {
      totalCount @include(if: $withTotal)
      pageInfo {
        hasNextPage
        endCursor
      }
      nodes {
        id
        name
        type
        elevation
        geom
      }
    }
  }
`;

export type MapPlacesPageResult = { places: Connection<PlacePin> };

// mainGeom is the expensive field (simplified track geometry), so it is only
// requested when the tracks layer is on.
export const MAP_ROUTES_PAGE = gql`
  query MapRoutesPage($filter: RouteFilter, $first: Int!, $offset: Int!, $withTracks: Boolean!, $withTotal: Boolean!) {
    routes(first: $first, offset: $offset, orderBy: [ID_ASC], filter: $filter) {
      totalCount @include(if: $withTotal)
      pageInfo {
        hasNextPage
        endCursor
      }
      nodes {
        id
        name
        activities
        durationMin
        startPoint
        mainGeom(toleranceM: 15) @include(if: $withTracks)
      }
    }
  }
`;

export type MapRoutesPageResult = { routes: Connection<RoutePin> };

export const MAP_REGIONS_PAGE = gql`
  query MapRegionsPage($filter: RegionFilter, $first: Int!, $offset: Int!, $withTotal: Boolean!) {
    regions(first: $first, offset: $offset, orderBy: [ID_ASC], filter: $filter) {
      totalCount @include(if: $withTotal)
      pageInfo {
        hasNextPage
        endCursor
      }
      nodes {
        id
        name
        type
        geomSimple(toleranceM: 100)
      }
    }
  }
`;

export type MapRegionsPageResult = { regions: Connection<RegionShape> };

export const MAP_OUTINGS_PAGE = gql`
  query MapOutingsPage($filter: OutingFilter, $first: Int!, $offset: Int!, $withTotal: Boolean!) {
    outings(first: $first, offset: $offset, orderBy: [ID_ASC], filter: $filter) {
      totalCount @include(if: $withTotal)
      pageInfo {
        hasNextPage
        endCursor
      }
      nodes {
        id
        title
        activity
        status
        date
        rating
        point
      }
    }
  }
`;

export type MapOutingsPageResult = { outings: Connection<OutingPin> };

export type EventPin = {
  id: string;
  title: string;
  startsAt: string;
  location: GeoPoint | null;
};

// Only upcoming events that opted in (showOnMap) and have a meeting point are
// mapped; the caller builds that filter in toEventMapFilter.
export const MAP_EVENTS_PAGE = gql`
  query MapEventsPage($filter: EventFilter, $first: Int!, $offset: Int!, $withTotal: Boolean!) {
    events(first: $first, offset: $offset, orderBy: [ID_ASC], filter: $filter) {
      totalCount @include(if: $withTotal)
      pageInfo {
        hasNextPage
        endCursor
      }
      nodes {
        id
        title
        startsAt
        location
      }
    }
  }
`;

export type MapEventsPageResult = { events: Connection<EventPin> };

export const SEARCH = gql`
  query Search($q: String!) {
    places(filter: { name: { likeInsensitive: $q } }, first: 5, orderBy: [NAME_ASC]) {
      nodes {
        id
        name
        i18n
        type
        elevation
        geom
      }
    }
    regions(filter: { name: { likeInsensitive: $q } }, first: 5, orderBy: [NAME_ASC]) {
      nodes {
        id
        name
        i18n
        type
      }
    }
    routes(filter: { name: { likeInsensitive: $q } }, first: 8, orderBy: [NAME_ASC]) {
      nodes {
        id
        name
        i18n
        activities
        startPoint
      }
    }
    people(filter: { displayName: { likeInsensitive: $q } }, first: 5) {
      nodes {
        id
        displayName
        activities
      }
    }
    outings(filter: { title: { likeInsensitive: $q } }, first: 5, orderBy: [DATE_DESC]) {
      nodes {
        id
        title
        date
        activity
        status
      }
    }
  }
`;

export type SearchResult = {
  places: { nodes: PlacePin[] };
  regions: { nodes: { id: string; name: string; i18n?: EntityI18n; type: string }[] };
  routes: { nodes: RoutePin[] };
  people: { nodes: { id: string; displayName: string | null; activities: Activity[] | null }[] };
  outings: { nodes: { id: string; title: string; date: string; activity: Activity; status: OutingStatus }[] };
};

// --- entity pages ---

// `displayName` is the security-definer column (publicly readable); the
// `person` relation also resolves publicly (person rows are public since API
// migration 0012) and is what carries `avatarKey`.
export type OutingPersonNode = {
  id: string;
  role: "OWNER" | "PARTICIPANT";
  personId: string;
  displayName: string | null;
  person: { avatarKey: string | null } | null;
};

// The reverse relation from outing is plain `outingPhotos` (not
// `outingPhotosByOutingId`) — pdbq drops the FK suffix when only one relation
// points at the parent. Reads follow outing visibility; the image bytes come
// from the /photos proxy via `photoUrl(storageKey)`.
export type OutingPhotoNode = { id: string; storageKey: string; caption: string | null; sort: number };

export type OutingCardData = {
  id: string;
  title: string;
  date: string;
  activity: Activity;
  status: OutingStatus;
  conditions: ConditionRating | null;
  rating: number | null;
  description: string | null;
  visibility: OutingVisibility;
  commentCount: number;
  likeCount: number;
  viewerHasLiked: boolean;
  outingPeopleByOutingId: { nodes: OutingPersonNode[] };
  outingRoutesByOutingId?: { nodes: { route: { id: string; name: string } }[] };
  outingPhotos: { totalCount: number; nodes: { id: string; storageKey: string }[] };
};

export const OUTING_CARD_FIELDS = gql`
  fragment OutingCardFields on Outing {
    id
    title
    date
    activity
    status
    conditions
    rating
    description
    visibility
    commentCount
    likeCount
    viewerHasLiked
    outingPeopleByOutingId {
      nodes {
        id
        role
        personId
        displayName
        person {
          avatarKey
        }
      }
    }
    outingRoutesByOutingId(orderBy: [SORT_ASC]) {
      nodes {
        route {
          id
          name
        }
      }
    }
    outingPhotos(first: 1, orderBy: [SORT_ASC, CREATED_AT_ASC]) {
      totalCount
      nodes {
        id
        storageKey
      }
    }
  }
`;

export const ROUTE_DETAIL = gql`
  query RouteDetail($id: UUID!) {
    route(id: $id) {
      id
      name
      i18n
      description
      activities
      grades
      orientation
      durationMin
      createdAt
      updatedAt
      createdBy
      # Same relation naming as place: pdbq calls the FK relation
      # \`createdByRow\`. Null for imported routes (created_by is null).
      createdByRow {
        id
        displayName
      }
      startPoint
      avgRating
      ratingCount
      mainGeom(toleranceM: 5)
      # track.geom is the raw MultiLineStringZ — mainGeom drops Z, so the
      # elevation profile needs the untouched track geometry. pointTimes (an
      # ISO-timestamp array aligned 1:1 with the geometry points, null for
      # curated lines) feeds the profile's time axis.
      routeTracksByRouteId {
        nodes {
          id
          role
          label
          track {
            id
            distanceM
            ascentM
            descentM
            elevationMinM
            elevationMaxM
            geom
            pointTimes
          }
        }
      }
      routePlacesByRouteId {
        nodes {
          id
          role
          place {
            id
            name
            i18n
            type
            elevation
          }
        }
      }
      routeRelationsByRouteId {
        nodes {
          id
          type
          relatedRoute {
            id
            name
            i18n
          }
        }
      }
      # Public ticks only: RLS also returns the viewer's own private ticks, so
      # without the filter the owner would see a higher count than everyone else.
      routeTicksByRouteId(filter: { isPublic: { equalTo: true } }) {
        totalCount
      }
      # 20 outings x 4 photos caps the community photo grid (F23); pdbq prices
      # nested queries by the product of first: args, and this shape passes.
      # The alias avoids an arg conflict with the fragment's outingPhotos(first: 1).
      # totalCounts let the UI say when the sample is truncated.
      outingRoutesByRouteId(first: 20) {
        totalCount
        nodes {
          style
          result
          outing {
            ...OutingCardFields
            photos: outingPhotos(first: 4, orderBy: [SORT_ASC, CREATED_AT_ASC]) {
              totalCount
              nodes {
                id
                storageKey
                caption
              }
            }
          }
        }
      }
    }
  }
  ${OUTING_CARD_FIELDS}
`;

export type TrackStats = {
  id: string;
  distanceM: number | null;
  ascentM: number | null;
  descentM: number | null;
  elevationMinM: number | null;
  elevationMaxM: number | null;
};

export type RouteDetailResult = {
  route: {
    id: string;
    name: string;
    i18n?: EntityI18n;
    description: string | null;
    activities: Activity[];
    grades: Record<string, string>;
    orientation: string | null;
    durationMin: number | null;
    createdAt: string;
    updatedAt: string;
    createdBy: string | null;
    createdByRow: { id: string; displayName: string | null } | null;
    startPoint: GeoPoint | null;
    // BigFloat — pdbq serializes numerics as strings ("4.5000000000000000").
    avgRating: string | null;
    ratingCount: number;
    mainGeom: Geometry | null;
    routeTracksByRouteId: { nodes: { id: string; role: string; label: string | null; track: TrackStats & { geom: Geometry | null; pointTimes: string[] | null } }[] };
    routePlacesByRouteId: { nodes: { id: string; role: string; place: { id: string; name: string; i18n?: EntityI18n; type: PlaceType; elevation: number | null } }[] };
    routeRelationsByRouteId: { nodes: { id: string; type: string; relatedRoute: { id: string; name: string; i18n?: EntityI18n } }[] };
    routeTicksByRouteId: { totalCount: number };
    outingRoutesByRouteId: {
      totalCount: number;
      nodes: {
        style: string | null;
        result: string | null;
        outing: OutingCardData & {
          photos: { totalCount: number; nodes: { id: string; storageKey: string; caption: string | null }[] };
        };
      }[];
    };
  } | null;
};

// --- user-drawn routes (F43) ---
//
// TODO(pdbq): enum ARRAYS are not name-mapped on input. Scalar enum arguments
// accept GraphQL member names (`role: MAIN`, `activity: HIKING` — inline or
// via variables), but a `[Activity!]` array variable is passed through to
// Postgres verbatim, so the UPPERCASE member names fail with "invalid input
// value for enum activity". Until pdbq maps array elements, `activities` must
// be sent as the raw lowercase database labels via variables — `["hiking"]`,
// NOT `[HIKING]`. Outputs are unaffected (always UPPERCASE). Applies to both
// RouteCreateInput.activities and RouteUpdateInput.activities; use
// activitiesToWire() below for every write.
//
// Verified against the live API: lowercase labels insert correctly and read
// back as UPPERCASE members.

/** Workaround for the pdbq enum-array bug above: "HIKING" → "hiking". */
export function activitiesToWire(activities: Activity[]): string[] {
  return activities.map((a) => a.toLowerCase());
}

// RLS (route_member_insert) requires createdBy to equal the caller's
// person_id claim; nothing defaults it server-side. Update/delete are
// creator-only (created_by = viewer).
export const CREATE_ROUTE = gql`
  mutation CreateRoute($input: RouteCreateInput!) {
    createRoute(input: $input) {
      route {
        id
      }
    }
  }
`;

export type CreateRouteResult = { createRoute: { route: { id: string } } };

// Scalar enums are name-mapped normally, so role: MAIN inline is fine — only
// enum *arrays* need the lowercase workaround (see TODO(pdbq) above).
export const CREATE_ROUTE_TRACK = gql`
  mutation CreateRouteTrack($routeId: UUID!, $trackId: UUID!, $sort: Int!) {
    createRouteTrack(input: { routeId: $routeId, trackId: $trackId, role: MAIN, sort: $sort }) {
      routeTrack {
        id
      }
    }
  }
`;

export const CREATE_ROUTE_PLACE = gql`
  mutation CreateRoutePlace($routeId: UUID!, $placeId: UUID!) {
    createRoutePlace(input: { routeId: $routeId, placeId: $placeId }) {
      routePlace {
        id
      }
    }
  }
`;

export const UPDATE_ROUTE = gql`
  mutation UpdateRoute($id: UUID!, $patch: RouteUpdateInput!) {
    updateRoute(id: $id, patch: $patch) {
      route {
        id
      }
    }
  }
`;

export type UpdateRouteResult = { updateRoute: { route: { id: string } | null } | null };

// Fails with 23503 (FK RESTRICT on outing_route) when any outing references
// the route — the UI maps that to a friendly message rather than retrying.
export const DELETE_ROUTE = gql`
  mutation DeleteRoute($id: UUID!) {
    deleteRoute(id: $id) {
      route {
        id
      }
    }
  }
`;

// Places-only typeahead for linking places to a new route. Callers pass
// `%query%` (likeInsensitive needs explicit wildcards).
export const PLACE_PICKER_SEARCH = gql`
  query PlacePicker($q: String!) {
    places(filter: { name: { likeInsensitive: $q } }, first: 8, orderBy: [NAME_ASC]) {
      nodes {
        id
        name
        type
        elevation
      }
    }
  }
`;

export type PlacePickerResult = {
  places: { nodes: { id: string; name: string; type: PlaceType; elevation: number | null }[] };
};

export const PLACE_DETAIL = gql`
  query PlaceDetail($id: UUID!) {
    place(id: $id) {
      id
      name
      i18n
      type
      elevation
      geom
      createdAt
      updatedAt
      createdBy
      # pdbq names the FK relation \`createdByRow\` (column name + Row), not
      # \`creator\`. Null for imported places (created_by is null) and after the
      # creator deletes their account (FK is on delete set null).
      createdByRow {
        id
        displayName
        avatarKey
      }
      placeRegionsByPlaceId {
        nodes {
          region {
            id
            name
            i18n
            type
          }
        }
      }
      routePlacesByPlaceId {
        nodes {
          role
          route {
            id
            name
            i18n
            activities
            durationMin
            distanceM
            ascentM
          }
        }
      }
      trackPlacesByPlaceId(first: 30) {
        totalCount
        nodes {
          distanceM
          track {
            routeTracksByTrackId {
              nodes {
                role
                route {
                  id
                  name
                  i18n
                  activities
                  durationMin
                  distanceM
                  ascentM
                }
              }
            }
          }
        }
      }
    }
  }
`;

// distanceM/ascentM are optional because only some documents (PLACE_DETAIL)
// select them; pickers and search results don't.
export type RouteRef = {
  id: string;
  name: string;
  i18n?: EntityI18n;
  activities: Activity[];
  durationMin: number | null;
  distanceM?: number | null;
  ascentM?: number | null;
};

export type PlaceDetailResult = {
  place: {
    id: string;
    name: string;
    i18n?: EntityI18n;
    type: PlaceType;
    elevation: number | null;
    geom: GeoPoint;
    createdAt: string;
    updatedAt: string;
    createdBy: string | null;
    createdByRow: { id: string; displayName: string | null; avatarKey: string | null } | null;
    placeRegionsByPlaceId: { nodes: { region: { id: string; name: string; i18n?: EntityI18n; type: string } }[] };
    routePlacesByPlaceId: { nodes: { role: string; route: RouteRef }[] };
    trackPlacesByPlaceId: {
      totalCount: number;
      nodes: { distanceM: number; track: { routeTracksByTrackId: { nodes: { role: string; route: RouteRef }[] } } }[];
    };
  } | null;
};

// --- user-created places (F41) ---
//
// RLS (place_member_insert) requires createdBy to equal the caller's own
// person_id claim — nothing defaults it server-side, so the input must carry
// it. Updates/deletes are creator-only (place_owner_update/_delete), with
// admin overrides; the UI additionally gates on createdBy === viewer.
//
// Geometry input, verified against the live API: the `GeoJSON` scalar accepts
// either a GeoJSON *object* ({ type: "Point", coordinates: [lng, lat] }) or a
// WKT *string* ("POINT(lng lat)"). A GeoJSON document passed as a JSON string
// is rejected — pdbq parses string inputs as WKT ("spatial value ... is not
// valid WKT"). We send the GeoJSON object form. Output is always GeoJSON.
export const CREATE_PLACE = gql`
  mutation CreatePlace($input: PlaceCreateInput!) {
    createPlace(input: $input) {
      place {
        id
      }
    }
  }
`;

export type CreatePlaceResult = { createPlace: { place: { id: string } } };

export const UPDATE_PLACE = gql`
  mutation UpdatePlace($id: UUID!, $patch: PlaceUpdateInput!) {
    updatePlace(id: $id, patch: $patch) {
      place {
        id
        name
        type
        elevation
      }
    }
  }
`;

export const DELETE_PLACE = gql`
  mutation DeletePlace($id: UUID!) {
    deletePlace(id: $id) {
      place {
        id
      }
    }
  }
`;

export const REGION_DETAIL = gql`
  query RegionDetail($id: UUID!) {
    region(id: $id) {
      id
      name
      i18n
      type
      createdAt
      updatedAt
      geomSimple(toleranceM: 50)
      parent {
        id
        name
        i18n
        type
      }
      regionsByParentId: regions {
        nodes {
          id
          name
          i18n
          type
        }
      }
      placeRegionsByRegionId(first: 100) {
        totalCount
        nodes {
          place {
            id
            name
            i18n
            type
            elevation
            geom
          }
        }
      }
    }
  }
`;

export type RegionDetailResult = {
  region: {
    id: string;
    name: string;
    i18n?: EntityI18n;
    type: string;
    createdAt: string;
    updatedAt: string;
    geomSimple: Geometry | null;
    parent: { id: string; name: string; i18n?: EntityI18n; type: string } | null;
    regionsByParentId: { nodes: { id: string; name: string; i18n?: EntityI18n; type: string }[] };
    placeRegionsByRegionId: { totalCount: number; nodes: { place: PlacePin }[] };
  } | null;
};

export const OUTING_DETAIL = gql`
  query OutingDetail($id: UUID!) {
    outing(id: $id) {
      id
      title
      description
      date
      activity
      status
      conditions
      rating
      elapsedSeconds
      visibility
      createdAt
      updatedAt
      point
      commentCount
      likeCount
      viewerHasLiked
      track {
        id
        distanceM
        ascentM
        descentM
        elevationMinM
        elevationMaxM
        geom
        pointTimes
      }
      outingPeopleByOutingId {
        nodes {
          id
          role
          personId
          displayName
          person {
            avatarKey
          }
        }
      }
      outingPhotos(first: 50, orderBy: [SORT_ASC, CREATED_AT_ASC]) {
        nodes {
          id
          storageKey
          caption
          sort
        }
      }
      outingRoutesByOutingId(orderBy: [SORT_ASC]) {
        nodes {
          id
          sort
          style
          result
          route {
            id
            name
            i18n
            activities
            durationMin
            mainGeom(toleranceM: 5)
            # geom carries the Z coordinates the elevation profile needs when
            # the outing has no recorded track of its own.
            routeTracksByRouteId {
              nodes {
                role
                track {
                  id
                  distanceM
                  ascentM
                  descentM
                  geom
                  pointTimes
                }
              }
            }
          }
        }
      }
    }
  }
`;

export type OutingDetailResult = {
  outing: {
    id: string;
    title: string;
    description: string | null;
    date: string;
    activity: Activity;
    status: OutingStatus;
    conditions: ConditionRating | null;
    rating: number | null;
    elapsedSeconds: number | null;
    visibility: OutingVisibility;
    createdAt: string;
    updatedAt: string;
    point: GeoPoint | null;
    commentCount: number;
    likeCount: number;
    viewerHasLiked: boolean;
    track: (TrackStats & { geom: Geometry; pointTimes: string[] | null }) | null;
    outingPeopleByOutingId: { nodes: OutingPersonNode[] };
    outingPhotos: { nodes: OutingPhotoNode[] };
    outingRoutesByOutingId: {
      nodes: {
        id: string;
        sort: number;
        style: ClimbStyle | null;
        result: string | null;
        route: RouteRef & {
          mainGeom: Geometry | null;
          routeTracksByRouteId: { nodes: { role: string; track: { id: string; distanceM: number | null; ascentM: number | null; descentM: number | null; geom: Geometry | null; pointTimes: string[] | null } }[] };
        };
      }[];
    };
  } | null;
};

// --- feed ---

export const FEED = gql`
  query Feed($first: Int!, $offset: Int!, $filter: OutingFilter, $orderBy: [OutingsOrderBy!] = [DATE_DESC]) {
    outings(first: $first, offset: $offset, orderBy: $orderBy, filter: $filter) {
      totalCount
      nodes {
        ...OutingCardFields
      }
    }
  }
  ${OUTING_CARD_FIELDS}
`;

export type FeedResult = { outings: { totalCount: number; nodes: OutingCardData[] } };

// Step one of the "Following" feed: who the viewer follows. The feed itself
// reuses FEED with a relation filter —
//   outingPeopleByOutingId: { some: { role: { equalTo: OWNER }, personId: { in: $ids } } }
// — so an outing shows up when a followee *owns* it, not when they merely
// participated.
export const MY_FOLLOWEES = gql`
  query MyFollowees($viewerId: UUID!) {
    personFollows(filter: { followerId: { equalTo: $viewerId } }, first: 500) {
      nodes {
        followeeId
      }
    }
  }
`;

export type MyFolloweesResult = { personFollows: { nodes: { followeeId: string }[] } };

// --- profile ---

// Slim header query for the user-menu avatar; MY_PROFILE drags in outings.
export const NAV_PERSON = gql`
  query NavPerson($id: UUID!) {
    person(id: $id) {
      id
      displayName
      avatarKey
    }
  }
`;

export type NavPersonResult = {
  person: { id: string; displayName: string | null; avatarKey: string | null } | null;
};

// No `email`: person is publicly readable as of API migration 0012, and RLS
// filters rows rather than columns, so `email` carries no select grant for any
// API role at all — asking for it fails the whole request, owner included.
export const MY_PROFILE = gql`
  query MyProfile($id: UUID!) {
    person(id: $id) {
      id
      displayName
      activities
      avatarKey
      createdAt
    }
    outingPeople(filter: { personId: { equalTo: $id } }, first: 50) {
      totalCount
      nodes {
        role
        outing {
          ...OutingCardFields
        }
      }
    }
  }
  ${OUTING_CARD_FIELDS}
`;

export type MyProfileResult = {
  person: { id: string; displayName: string | null; activities: Activity[] | null; avatarKey: string | null; createdAt: string } | null;
  outingPeople: { totalCount: number; nodes: { role: "OWNER" | "PARTICIPANT"; outing: OutingCardData }[] };
};

export const UPDATE_PROFILE = gql`
  mutation UpdateProfile($id: UUID!, $displayName: String) {
    updatePerson(id: $id, patch: { displayName: $displayName }) {
      person {
        id
        displayName
      }
    }
  }
`;

// Self-service avatar (F22): avatarKey points at a photod upload (same flow
// as outing photos — uploadPhoto → storage_key). Passing null clears it;
// replacing or clearing orphans the old file on disk, which is acceptable
// (keys are random and unlisted).
export const UPDATE_AVATAR = gql`
  mutation UpdateAvatar($id: UUID!, $avatarKey: String) {
    updatePerson(id: $id, patch: { avatarKey: $avatarKey }) {
      person {
        id
        avatarKey
      }
    }
  }
`;

export type UpdateAvatarResult = { updatePerson: { person: { id: string; avatarKey: string | null } | null } | null };

// --- create outing ---

export const ROUTE_PICKER_SEARCH = gql`
  query RoutePicker($q: String!) {
    routes(filter: { name: { likeInsensitive: $q } }, first: 8, orderBy: [NAME_ASC]) {
      nodes {
        id
        name
        activities
        durationMin
      }
    }
  }
`;

export type RoutePickerResult = { routes: { nodes: RouteRef[] } };

export const ROUTE_REF = gql`
  query RouteRef($id: UUID!) {
    route(id: $id) {
      id
      name
      activities
      durationMin
    }
  }
`;

export type RouteRefResult = { route: RouteRef | null };

// Search people for the participant picker. `likeInsensitive` needs explicit
// `%` wildcards — callers pass `%query%`.
export const PEOPLE_SEARCH = gql`
  query PeopleSearch($q: String!) {
    people(filter: { displayName: { likeInsensitive: $q } }, first: 8) {
      nodes {
        id
        displayName
        activities
        avatarKey
      }
    }
  }
`;

export type PeopleSearchResult = {
  people: { nodes: { id: string; displayName: string | null; activities: Activity[] | null; avatarKey: string | null }[] };
};

// Note: the outing_route link is created in a second mutation — nesting it
// here fails RLS because the owner outing_person row isn't visible yet when
// the nested outing_route insert runs. The same applies to participant
// outing_person rows (verified against the live API): pdbq inserts nested
// rows in one statement, so the owner row isn't visible to the participant
// row's insert-policy check even when it comes first in the create array.
// Only the OWNER row may be nested; participants are follow-up mutations.
export const CREATE_OUTING = gql`
  mutation CreateOuting($input: OutingCreateInput!) {
    createOuting(input: $input) {
      outing {
        id
      }
    }
  }
`;

export type CreateOutingResult = { createOuting: { outing: { id: string } } };

// One row per route done on the outing; `sort` is the list position in the
// form, `style` is the ClimbStyle enum, `result` a free-text column.
export const CREATE_OUTING_ROUTE = gql`
  mutation CreateOutingRoute($outingId: UUID!, $routeId: UUID!, $sort: Int!, $style: ClimbStyle, $result: String) {
    createOutingRoute(
      input: { outingId: $outingId, routeId: $routeId, sort: $sort, style: $style, result: $result }
    ) {
      outingRoute {
        id
      }
    }
  }
`;

// Update/delete by the (outing_id, route_id) natural key so the edit form's
// diff needs no row-id bookkeeping. RLS (outing_route_owner_update/_delete)
// scopes both to the outing's owner.
export const UPDATE_OUTING_ROUTE = gql`
  mutation UpdateOutingRoute($outingId: UUID!, $routeId: UUID!, $patch: OutingRouteUpdateInput!) {
    updateOutingRouteByOutingIdAndRouteId(outingId: $outingId, routeId: $routeId, patch: $patch) {
      outingRoute {
        id
      }
    }
  }
`;

export const DELETE_OUTING_ROUTE = gql`
  mutation DeleteOutingRoute($outingId: UUID!, $routeId: UUID!) {
    deleteOutingRouteByOutingIdAndRouteId(outingId: $outingId, routeId: $routeId) {
      outingRoute {
        id
      }
    }
  }
`;

export const DELETE_OUTING = gql`
  mutation DeleteOuting($id: UUID!) {
    deleteOuting(id: $id) {
      outing {
        id
      }
    }
  }
`;

// --- track import (importTrack) ---
//
// The only way to create a track: the plain track CRUD mutations exist in the
// schema but are permission-denied at runtime. importTrack parses the GeoJSON
// (LineString or MultiLineString, [lon, lat, ele]) server-side and derives all
// stats. Member-only — anonymous callers get 42501. There is no working
// deleteTrack either, so replacing an outing's track orphans the old row.
export const IMPORT_TRACK = gql`
  mutation ImportTrack($geojson: String!, $pointTimes: [Datetime], $sourceGpx: String) {
    importTrack(input: { geojson: $geojson, pointTimes: $pointTimes, sourceGpx: $sourceGpx }) {
      result {
        id
        distanceM
        ascentM
      }
    }
  }
`;

export type ImportTrackResult = {
  importTrack: { result: { id: string; distanceM: number | null; ascentM: number | null } };
};

// Fetched lazily on export click, not as part of the detail queries —
// sourceGpx is the verbatim uploaded file and can be large.
export const TRACK_SOURCE_GPX = gql`
  query TrackSourceGpx($id: UUID!) {
    track(id: $id) {
      id
      sourceGpx
    }
  }
`;

export type TrackSourceGpxResult = { track: { id: string; sourceGpx: string | null } | null };

// --- edit outing ---
//
// pdbq names the payload type `<Table>UpdateInput` even though the argument
// is `patch:`. RLS scopes updates to the owner; a non-owner gets an error or
// a null `updateOuting` back rather than a changed row.
export const UPDATE_OUTING = gql`
  mutation UpdateOuting($id: UUID!, $patch: OutingUpdateInput!) {
    updateOuting(id: $id, patch: $patch) {
      outing {
        id
      }
    }
  }
`;

export type UpdateOutingResult = { updateOuting: { outing: { id: string } | null } | null };

// Participant tagging on an existing outing. Insert is owner-only via RLS
// (outing_person_owner_insert), and only role PARTICIPANT is insertable this
// way. Delete uses the (outing_id, person_id) unique-constraint mutation so
// no row-id lookup is needed; RLS lets the owner remove participants and a
// participant remove themself.
export const CREATE_OUTING_PERSON = gql`
  mutation CreateOutingPerson($outingId: UUID!, $personId: UUID!) {
    createOutingPerson(input: { outingId: $outingId, personId: $personId, role: PARTICIPANT }) {
      outingPerson {
        id
      }
    }
  }
`;

export const DELETE_OUTING_PERSON = gql`
  mutation DeleteOutingPerson($outingId: UUID!, $personId: UUID!) {
    deleteOutingPersonByOutingIdAndPersonId(outingId: $outingId, personId: $personId) {
      outingPerson {
        id
      }
    }
  }
`;

// --- ticklist (route_tick) ---
//
// RLS: the caller always sees their own rows, and rows with `isPublic: true`
// are visible to everyone (including anonymous). MY_TICKLIST still relies on
// the owner policy — its personId filter exists to keep the index in play —
// while PUBLIC_TICKLIST must filter `isPublic` explicitly, or an owner viewing
// their own profile would see private ticks a visitor wouldn't.
//
// `personId` must be sent explicitly on writes: RLS checks it against the
// JWT claim, but pdbq has no column default that fills it in.

export type TickListEntry = {
  id: string;
  priority: TickPriority;
  note: string | null;
  targetSeason: string | null;
  doneAt: string | null;
  isPublic: boolean;
  createdAt: string;
  route: {
    id: string;
    name: string;
    activities: Activity[];
    durationMin: number | null;
    routeTracksByRouteId: { nodes: { track: { distanceM: number | null; ascentM: number | null } }[] };
  };
};

export const MY_TICKLIST = gql`
  query MyTicklist($personId: UUID!) {
    routeTicks(filter: { personId: { equalTo: $personId } }, orderBy: [PRIORITY_ASC, CREATED_AT_DESC], first: 200) {
      totalCount
      nodes {
        id
        priority
        note
        targetSeason
        doneAt
        isPublic
        createdAt
        route {
          id
          name
          activities
          durationMin
          routeTracksByRouteId(first: 1) {
            nodes {
              track {
                distanceM
                ascentM
              }
            }
          }
        }
      }
    }
  }
`;

export type MyTicklistResult = { routeTicks: { totalCount: number; nodes: TickListEntry[] } };

// A person's public ticks, for their profile page. Readable by anyone via the
// public-tick RLS policy; the isPublic filter keeps the owner's own private
// rows out when they look at their own profile.
export const PUBLIC_TICKLIST = gql`
  query PublicTicklist($personId: UUID!) {
    routeTicks(
      filter: { personId: { equalTo: $personId }, isPublic: { equalTo: true } }
      orderBy: [PRIORITY_ASC, CREATED_AT_DESC]
      first: 100
    ) {
      totalCount
      nodes {
        id
        priority
        targetSeason
        doneAt
        route {
          id
          name
          activities
          durationMin
        }
      }
    }
  }
`;

export type PublicTickEntry = {
  id: string;
  priority: TickPriority;
  targetSeason: string | null;
  doneAt: string | null;
  route: { id: string; name: string; activities: Activity[]; durationMin: number | null };
};

export type PublicTicklistResult = { routeTicks: { totalCount: number; nodes: PublicTickEntry[] } };

// Drives the tick button on a route page. Returns null when the route isn't
// on the caller's list (and always null for anonymous, via RLS).
export const MY_TICK_FOR_ROUTE = gql`
  query MyTickForRoute($personId: UUID!, $routeId: UUID!) {
    routeTickByPersonIdAndRouteId(personId: $personId, routeId: $routeId) {
      id
      priority
      note
      targetSeason
      doneAt
      isPublic
    }
  }
`;

export type MyTickForRouteResult = {
  routeTickByPersonIdAndRouteId: {
    id: string;
    priority: TickPriority;
    note: string | null;
    targetSeason: string | null;
    doneAt: string | null;
    isPublic: boolean;
  } | null;
};

// Upsert keyed on the (person_id, route_id) unique constraint, so "add to
// ticklist" is idempotent and re-adding edits the existing row instead of
// failing on the constraint.
export const UPSERT_TICK = gql`
  mutation UpsertTick(
    $personId: UUID!
    $routeId: UUID!
    $priority: TickPriority!
    $note: String
    $targetSeason: String
    $isPublic: Boolean!
  ) {
    upsertRouteTickByPersonIdAndRouteId(
      input: {
        personId: $personId
        routeId: $routeId
        priority: $priority
        note: $note
        targetSeason: $targetSeason
        isPublic: $isPublic
      }
    ) {
      routeTick {
        id
        priority
        note
        targetSeason
        doneAt
        isPublic
      }
    }
  }
`;

export const UPDATE_TICK = gql`
  mutation UpdateTick($id: UUID!, $patch: RouteTickUpdateInput!) {
    updateRouteTick(id: $id, patch: $patch) {
      routeTick {
        id
        priority
        note
        targetSeason
        doneAt
      }
    }
  }
`;

// Delete by natural key so the route page can un-tick without first
// resolving the row id.
export const DELETE_TICK_FOR_ROUTE = gql`
  mutation DeleteTickForRoute($personId: UUID!, $routeId: UUID!) {
    deleteRouteTickByPersonIdAndRouteId(personId: $personId, routeId: $routeId) {
      routeTick {
        id
      }
    }
  }
`;

// --- saved places (saved_place) ---
//
// Owner-private map bookmarks — no public select policy at all, so queries
// need no personId filter for correctness; MY_SAVED_PLACES passes one only to
// keep the (person_id, created_at) index in play. Writes must send personId
// explicitly (RLS checks it against the JWT claim; nothing defaults it
// server-side). A row is either a saved existing place (placeId) or a named
// custom map position (name + geom) — the DB check constraint enforces the
// either/or, so the create input carries one shape or the other, never both.

export type SavedPlaceEntry = {
  id: string;
  name: string | null;
  geom: GeoPoint | null;
  createdAt: string;
  place: {
    id: string;
    name: string;
    i18n?: EntityI18n;
    type: PlaceType;
    elevation: number | null;
    geom: GeoPoint;
  } | null;
};

export const MY_SAVED_PLACES = gql`
  query MySavedPlaces($personId: UUID!) {
    savedPlaces(filter: { personId: { equalTo: $personId } }, orderBy: [CREATED_AT_DESC], first: 200) {
      totalCount
      nodes {
        id
        name
        geom
        createdAt
        place {
          id
          name
          i18n
          type
          elevation
          geom
        }
      }
    }
  }
`;

export type MySavedPlacesResult = { savedPlaces: { totalCount: number; nodes: SavedPlaceEntry[] } };

// Drives the save button on a place page. Empty when the place isn't saved
// (and always empty for anonymous, via RLS).
export const MY_SAVED_FOR_PLACE = gql`
  query MySavedForPlace($placeId: UUID!) {
    savedPlaces(filter: { placeId: { equalTo: $placeId } }, first: 1) {
      nodes {
        id
      }
    }
  }
`;

export type MySavedForPlaceResult = { savedPlaces: { nodes: { id: string }[] } };

export const CREATE_SAVED_PLACE = gql`
  mutation CreateSavedPlace($input: SavedPlaceCreateInput!) {
    createSavedPlace(input: $input) {
      savedPlace {
        id
      }
    }
  }
`;

// Rename is for custom positions only — saved places display the place name.
export const UPDATE_SAVED_PLACE = gql`
  mutation UpdateSavedPlace($id: UUID!, $name: String!) {
    updateSavedPlace(id: $id, patch: { name: $name }) {
      savedPlace {
        id
        name
      }
    }
  }
`;

export const DELETE_SAVED_PLACE = gql`
  mutation DeleteSavedPlace($id: UUID!) {
    deleteSavedPlace(id: $id) {
      savedPlace {
        id
      }
    }
  }
`;

// --- people & follows (person_follow) ---
//
// `person` gained a public select policy in API migration 0012, but only the
// safe columns are granted (id, displayName, activities, i18n, createdAt).
// `email`, `status` and the auth columns carry no select grant for any API
// role — RLS filters rows, not columns, so a grant would have exposed every
// address the moment the rows went public. Asking for `email` fails the whole
// request with "permission denied for table person", owner included.
//
// Follows are public to read (person_follow_public_select) and owner-scoped to
// write, so the follow/unfollow mutations must send followerId themselves;
// RLS checks it against the JWT claim but nothing defaults it server-side.

export type PersonSummary = {
  id: string;
  displayName: string | null;
  activities: Activity[] | null;
  avatarKey: string | null;
};

export const PERSON_SUMMARY_FIELDS = gql`
  fragment PersonSummaryFields on Person {
    id
    displayName
    activities
    avatarKey
  }
`;

export const PUBLIC_PROFILE = gql`
  query PublicProfile($id: UUID!, $viewerId: UUID) {
    person(id: $id) {
      ...PersonSummaryFields
      createdAt
    }
    followers: personFollows(filter: { followeeId: { equalTo: $id } }, first: 100, orderBy: [CREATED_AT_DESC]) {
      totalCount
      nodes {
        id
        createdAt
        follower {
          ...PersonSummaryFields
        }
      }
    }
    following: personFollows(filter: { followerId: { equalTo: $id } }, first: 100, orderBy: [CREATED_AT_DESC]) {
      totalCount
      nodes {
        id
        createdAt
        followee {
          ...PersonSummaryFields
        }
      }
    }
    outingPeople(filter: { personId: { equalTo: $id } }, first: 50) {
      totalCount
      nodes {
        role
        outing {
          ...OutingCardFields
        }
      }
    }
    # Whether the viewer already follows this person. Skipped entirely when
    # anonymous — $viewerId is null and the edge lookup would be meaningless.
    viewerFollow: personFollows(
      filter: { followerId: { equalTo: $viewerId }, followeeId: { equalTo: $id } }
      first: 1
    ) {
      nodes {
        id
      }
    }
  }
  ${PERSON_SUMMARY_FIELDS}
  ${OUTING_CARD_FIELDS}
`;

export type FollowEdge = { id: string; createdAt: string };

export type PublicProfileResult = {
  person: (PersonSummary & { createdAt: string }) | null;
  followers: { totalCount: number; nodes: (FollowEdge & { follower: PersonSummary })[] };
  following: { totalCount: number; nodes: (FollowEdge & { followee: PersonSummary })[] };
  outingPeople: { totalCount: number; nodes: { role: "OWNER" | "PARTICIPANT"; outing: OutingCardData }[] };
  viewerFollow: { nodes: { id: string }[] };
};

// --- profile map ---
//
// Outings for one person inside a date window, with enough geometry to draw a
// mini map: the outing's own recorded track, and the main geometry of any
// linked route for outings that were logged against a route without a GPX.
// Route geometry comes through the simplified `mainGeom` computed column;
// `track.geom` has no simplified variant on the API side, so the window sizes
// below double as the payload bound.
export const PROFILE_MAP = gql`
  query ProfileMap($id: UUID!, $from: Date, $first: Int!) {
    outingPeople(
      filter: { personId: { equalTo: $id }, outing: { date: { greaterThanOrEqualTo: $from } } }
      first: $first
    ) {
      totalCount
      nodes {
        outing {
          id
          title
          date
          activity
          point
          track {
            id
            geom
          }
          outingRoutesByOutingId {
            nodes {
              route {
                id
                mainGeom(toleranceM: 25)
              }
            }
          }
        }
      }
    }
  }
`;

export type ProfileMapOuting = {
  id: string;
  title: string;
  date: string;
  activity: Activity;
  point: GeoPoint | null;
  track: { id: string; geom: Geometry | null } | null;
  outingRoutesByOutingId: { nodes: { route: { id: string; mainGeom: Geometry | null } }[] };
};

export type ProfileMapResult = {
  outingPeople: { totalCount: number; nodes: { outing: ProfileMapOuting }[] };
};

// Upsert rather than insert so re-following an already-followed person is a
// no-op instead of a unique-violation, matching the ticklist's add behaviour.
export const FOLLOW_PERSON = gql`
  mutation FollowPerson($followerId: UUID!, $followeeId: UUID!) {
    upsertPersonFollowByFollowerIdAndFolloweeId(
      input: { followerId: $followerId, followeeId: $followeeId }
    ) {
      personFollow {
        id
      }
    }
  }
`;

// Delete by natural key so unfollowing needs no lookup round-trip.
export const UNFOLLOW_PERSON = gql`
  mutation UnfollowPerson($followerId: UUID!, $followeeId: UUID!) {
    deletePersonFollowByFollowerIdAndFolloweeId(followerId: $followerId, followeeId: $followeeId) {
      personFollow {
        id
      }
    }
  }
`;

// --- outing likes (outing_like) ---
//
// The row's existence is the like, so there is nothing to patch — liking is
// an upsert and unliking a delete, both keyed on (outingId, personId) so
// neither needs a lookup round-trip. Same shape as FOLLOW_PERSON.
//
// `likeCount` / `viewerHasLiked` are security-definer computed columns on
// outing, not aggregates over `outingLikesByOutingId` — a card shows the
// count without fetching rows, and `viewerHasLiked` reads the JWT claim
// server-side so the heart's filled state needs no viewer-keyed query. It is
// false for anonymous callers.
export const LIKE_OUTING = gql`
  mutation LikeOuting($outingId: UUID!, $personId: UUID!) {
    upsertOutingLikeByOutingIdAndPersonId(input: { outingId: $outingId, personId: $personId }) {
      outingLike {
        id
      }
    }
  }
`;

export const UNLIKE_OUTING = gql`
  mutation UnlikeOuting($outingId: UUID!, $personId: UUID!) {
    deleteOutingLikeByOutingIdAndPersonId(outingId: $outingId, personId: $personId) {
      outingLike {
        id
      }
    }
  }
`;

// --- outing comments (outing_comment) ---
//
// The thread is a self-referencing tree (`parentId` null at the root), and
// pdbq generates flat connections with no recursive CTE — so depth comes from
// the *document*: COMMENT_FIELDS is spliced in once per level, four levels
// deep, and the reverse relation is `outingCommentsByParentId`.
//
// Four levels is what the UI indents. A comment that still has replies at the
// deepest level renders a "Continue this thread" link instead, which re-runs
// this same query re-rooted at that comment — `$parentId` is the only thing
// that changes, so one document serves both the outing thread and every
// continuation. That is also why each level selects `replyCount`: the link
// only appears when the bottom row actually has children, and pdbq's
// `totalCount` answers that without fetching a fifth level.
//
// Soft-deleted comments are *not* filtered out: a tombstone has to stay in
// place or its replies lose their parent and vanish from the tree. The UI
// renders `deletedAt != null` as "[deleted]" and hides the reply/delete
// actions. `commentCount` on outing excludes them, so the badge counts only
// live comments.
//
// The per-level `first:` values shrink as the tree deepens (25/8/5/3) and are
// load-bearing, not a guess. pdbq prices a nested query as roughly the product
// of the `first:` arguments down each path, against a default ceiling of
// 100000, and rejects anything over it with "operation cost N exceeds limit
// 100000" — a request-time error that typechecking cannot catch. A flat 200 at
// four levels prices at 1.6 billion; even 60/20/10/5 costs 1625041.
//
// The `replyCount` sub-connection on every node roughly doubles the total, so
// it is part of this budget: measured against pdbq 0.8.2, 25/8/5/3 passes
// while 30/8/5/3 costs 113041 and fails. ~26 roots is the practical ceiling at
// this shape. Widening any level means narrowing another — re-measure rather
// than reason about it, since the cost model is the server's, not ours.
//
// Nothing is lost to these caps: a truncated level still reports its true
// `totalCount`, and "Continue this thread" re-roots the query at that comment,
// starting the cost budget over with a fresh four levels.

export type CommentAuthor = { id: string; displayName: string | null; avatarKey: string | null };

export type CommentNode = {
  id: string;
  body: string;
  depth: number;
  deletedAt: string | null;
  createdAt: string;
  personId: string;
  parentId: string | null;
  person: CommentAuthor | null;
  replyCount: { totalCount: number };
  replies?: { nodes: CommentNode[] };
};

// Kept as a plain string (not a gql`` document) because it is interpolated
// into the query below rather than sent on its own.
const COMMENT_FIELDS = `
  id
  body
  depth
  deletedAt
  createdAt
  personId
  parentId
  person {
    id
    displayName
    avatarKey
  }
  replyCount: outingCommentsByParentId {
    totalCount
  }
`;

// Oldest-first at every level, so a thread reads top to bottom the way it was
// written.
//
// There are two documents rather than one parameterized by `$parentId`,
// because pdbq gives `equalTo: null` a surprising meaning: a null variable
// makes the operator drop out of the generated SQL entirely instead of
// becoming `IS NULL`, so `parentId: { equalTo: $parentId }` with $parentId
// null matches *every* comment on the outing — every reply then renders as a
// root. Selecting the true roots needs `parentId: { isNull: true }`, which
// takes no variable, so the continuation query has to be its own document.
export const OUTING_COMMENTS = gql`
  query OutingComments($outingId: UUID!) {
    outingComments(
      filter: { outingId: { equalTo: $outingId }, parentId: { isNull: true } }
      orderBy: [CREATED_AT_ASC]
      first: 25
    ) {
      totalCount
      nodes {
        ${COMMENT_FIELDS}
        replies: outingCommentsByParentId(orderBy: [CREATED_AT_ASC], first: 8) {
          nodes {
            ${COMMENT_FIELDS}
            replies: outingCommentsByParentId(orderBy: [CREATED_AT_ASC], first: 5) {
              nodes {
                ${COMMENT_FIELDS}
                replies: outingCommentsByParentId(orderBy: [CREATED_AT_ASC], first: 3) {
                  nodes {
                    ${COMMENT_FIELDS}
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

// The continuation view: same four levels, rooted at one comment instead of
// at the outing. `parentId` is non-null here, so `equalTo` behaves normally
// and no outingId filter is needed — a parent id already pins the outing.
export const COMMENT_SUBTREE = gql`
  query CommentSubtree($parentId: UUID!) {
    outingComments(
      filter: { parentId: { equalTo: $parentId } }
      orderBy: [CREATED_AT_ASC]
      first: 25
    ) {
      totalCount
      nodes {
        ${COMMENT_FIELDS}
        replies: outingCommentsByParentId(orderBy: [CREATED_AT_ASC], first: 8) {
          nodes {
            ${COMMENT_FIELDS}
            replies: outingCommentsByParentId(orderBy: [CREATED_AT_ASC], first: 5) {
              nodes {
                ${COMMENT_FIELDS}
                replies: outingCommentsByParentId(orderBy: [CREATED_AT_ASC], first: 3) {
                  nodes {
                    ${COMMENT_FIELDS}
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export type OutingCommentsResult = {
  outingComments: { totalCount: number; nodes: CommentNode[] };
};

// `parentId` is nullable here: null posts a new top-level comment, a comment
// id posts a reply. Unlike the filter case above, a null *input* field is
// exactly what the column wants, so no special handling is needed.
export const CREATE_COMMENT = gql`
  mutation CreateComment($outingId: UUID!, $personId: UUID!, $parentId: UUID, $body: String!) {
    createOutingComment(
      input: { outingId: $outingId, personId: $personId, parentId: $parentId, body: $body }
    ) {
      outingComment {
        id
      }
    }
  }
`;

// Soft delete: the row survives so replies keep their parent. `body` is
// blanked in the same patch because the check constraint only tolerates an
// empty body once deletedAt is set, and the tombstone should not keep
// serving the original text to anyone reading the raw row.
//
// pdbq names the payload type `<Table>UpdateInput` even though the argument
// is `patch:`.
export const DELETE_COMMENT = gql`
  mutation DeleteComment($id: UUID!, $deletedAt: Datetime!) {
    updateOutingComment(id: $id, patch: { body: "", deletedAt: $deletedAt }) {
      outingComment {
        id
      }
    }
  }
`;

export const UPDATE_COMMENT = gql`
  mutation UpdateComment($id: UUID!, $body: String!) {
    updateOutingComment(id: $id, patch: { body: $body }) {
      outingComment {
        id
        body
        updatedAt
      }
    }
  }
`;

// Used by the "Continue this thread" view to render the comment it was
// re-rooted at as context above the replies, and to walk back up on exit.
export const COMMENT_CONTEXT = gql`
  query CommentContext($id: UUID!) {
    outingComment(id: $id) {
      id
      body
      depth
      deletedAt
      createdAt
      personId
      parentId
      person {
        id
        displayName
        avatarKey
      }
    }
  }
`;

export type CommentContextResult = {
  outingComment: {
    id: string;
    body: string;
    depth: number;
    deletedAt: string | null;
    createdAt: string;
    personId: string;
    parentId: string | null;
    person: CommentAuthor | null;
  } | null;
};

// --- notifications (notification) ---
//
// Recipient-private via RLS: a member only ever sees their own rows, so no
// recipientId filter is needed anywhere (and anonymous callers just get zero
// rows). Rows are written by DB triggers on likes/comments/follows and go away
// with their source — deleting the like/comment/follow cascades the
// notification, so "delete" never needs a UI.
//
// pdbq null tests are the long-form `isNull: true` operator; there is no
// variable-driven way to say IS NULL (a null variable drops the operator, see
// the comment-thread note above), so the unread filter is baked into the
// documents.

export type NotificationKind =
  | "OUTING_LIKE"
  | "OUTING_COMMENT"
  | "PERSON_FOLLOW"
  | "CLUB_INVITE"
  | "CLUB_JOIN_REQUEST"
  | "CLUB_JOIN_APPROVED"
  | "EVENT_INVITE";

export type NotificationNode = {
  id: string;
  kind: NotificationKind;
  actorId: string;
  outingId: string | null;
  commentId: string | null;
  clubId: string | null;
  eventId: string | null;
  readAt: string | null;
  createdAt: string;
  actor: { id: string; displayName: string | null; avatarKey: string | null } | null;
  outing: { id: string; title: string } | null;
  // Null when RLS hides the target from the recipient (e.g. an invite to a
  // HIDDEN club, where only the clubMember row is visible) — the UI falls
  // back to a generic "a group" / "an event" label.
  club: { id: string; name: string } | null;
  event: { id: string; title: string } | null;
};

export const UNREAD_NOTIFICATION_COUNT = gql`
  query UnreadNotificationCount {
    notifications(filter: { readAt: { isNull: true } }) {
      totalCount
    }
  }
`;

export type UnreadNotificationCountResult = { notifications: { totalCount: number } };

export const NOTIFICATIONS = gql`
  query Notifications {
    notifications(first: 20, orderBy: [CREATED_AT_DESC]) {
      totalCount
      nodes {
        id
        kind
        actorId
        outingId
        commentId
        clubId
        eventId
        readAt
        createdAt
        actor {
          id
          displayName
          avatarKey
        }
        outing {
          id
          title
        }
        club {
          id
          name
        }
        event {
          id
          title
        }
      }
    }
  }
`;

export type NotificationsResult = { notifications: { totalCount: number; nodes: NotificationNode[] } };

export const MARK_NOTIFICATION_READ = gql`
  mutation MarkNotificationRead($id: UUID!, $readAt: Datetime!) {
    updateNotification(id: $id, patch: { readAt: $readAt }) {
      notification {
        id
        readAt
      }
    }
  }
`;

// Bulk form: RLS scopes the filter to the caller's own rows, so this marks
// exactly the viewer's unread notifications, nobody else's.
export const MARK_ALL_NOTIFICATIONS_READ = gql`
  mutation MarkAllNotificationsRead($readAt: Datetime!) {
    updateNotifications(filter: { readAt: { isNull: true } }, patch: { readAt: $readAt }) {
      affectedCount
    }
  }
`;

export type MarkAllNotificationsReadResult = { updateNotifications: { affectedCount: number } };

// --- outing photos (outing_photo) ---
//
// The row references bytes uploaded to photod first (POST /photos through the
// same-origin proxy returns { storage_key }); reads follow outing visibility
// and writes are owner-only via RLS. Deleting a row orphans the file on disk,
// which is acceptable — photod keys are random and unlisted.

export const CREATE_OUTING_PHOTO = gql`
  mutation CreateOutingPhoto($outingId: UUID!, $storageKey: String!, $caption: String, $sort: Int!) {
    createOutingPhoto(input: { outingId: $outingId, storageKey: $storageKey, caption: $caption, sort: $sort }) {
      outingPhoto {
        id
      }
    }
  }
`;

export const UPDATE_OUTING_PHOTO = gql`
  mutation UpdateOutingPhoto($id: UUID!, $patch: OutingPhotoUpdateInput!) {
    updateOutingPhoto(id: $id, patch: $patch) {
      outingPhoto {
        id
        caption
        sort
      }
    }
  }
`;

export const DELETE_OUTING_PHOTO = gql`
  mutation DeleteOutingPhoto($id: UUID!) {
    deleteOutingPhoto(id: $id) {
      outingPhoto {
        id
      }
    }
  }
`;

// --- content reports (content_report) ---

/** `ContentEntity` enum members the report UI can target (the enum also has COMMENT). */
export type ReportEntityKind = "PLACE" | "REGION" | "ROUTE" | "OUTING" | "PERSON";

// RLS checks reporterId against the JWT claim; nothing defaults it server-side.
export const CREATE_CONTENT_REPORT = gql`
  mutation CreateContentReport($reporterId: UUID!, $entityKind: ContentEntity!, $entityId: UUID!, $body: String!) {
    createContentReport(input: { reporterId: $reporterId, entityKind: $entityKind, entityId: $entityId, body: $body }) {
      contentReport {
        id
      }
    }
  }
`;

// --- moderation (admin) ---

export type ContentEntityKind = ReportEntityKind | "COMMENT";
export type ReportStatus = "OPEN" | "RESOLVED" | "DISMISSED";
export type PersonStatus = "ACTIVE" | "SUSPENDED" | "BANNED" | "DELETED";

export type ContentReportRow = {
  id: string;
  reporterId: string;
  entityKind: ContentEntityKind;
  entityId: string;
  body: string;
  status: ReportStatus;
  createdAt: string;
  reporter: { id: string; displayName: string | null } | null;
};

// RLS scopes this: regular users see only their own reports, gaea_admin sees
// all. The `reporter` relation resolves displayName without a second query.
export const ADMIN_CONTENT_REPORTS = gql`
  query AdminContentReports($filter: ContentReportFilter) {
    contentReports(filter: $filter, orderBy: [CREATED_AT_DESC], first: 100) {
      totalCount
      nodes {
        id
        reporterId
        entityKind
        entityId
        body
        status
        createdAt
        reporter {
          id
          displayName
        }
      }
    }
  }
`;

export type AdminContentReportsResult = { contentReports: { totalCount: number; nodes: ContentReportRow[] } };

export const OPEN_REPORT_COUNT = gql`
  query OpenReportCount {
    contentReports(filter: { status: { equalTo: OPEN } }) {
      totalCount
    }
  }
`;

export type OpenReportCountResult = { contentReports: { totalCount: number } };

export const UPDATE_CONTENT_REPORT_STATUS = gql`
  mutation UpdateContentReportStatus($id: UUID!, $status: ReportStatus!) {
    updateContentReport(id: $id, patch: { status: $status }) {
      contentReport {
        id
        status
      }
    }
  }
`;

export type UpdateContentReportStatusResult = { updateContentReport: { contentReport: { id: string; status: ReportStatus } | null } };

// person.status carries a column grant for gaea_admin only (API migration
// 0041) — selecting it as anyone else fails the whole request with 42501, so
// this must stay a separate admin-gated document, never merged into a public
// person query. Batched over the PERSON reports on screen.
export const ADMIN_PERSON_STATUSES = gql`
  query AdminPersonStatuses($ids: [UUID!]) {
    people(filter: { id: { in: $ids } }, first: 100) {
      nodes {
        id
        status
      }
    }
  }
`;

export type AdminPersonStatusesResult = { people: { nodes: { id: string; status: PersonStatus }[] } };

// Security-definer admin function; the terse arg names (p = person id,
// s = new status) come from the SQL signature. Returns the person id.
export const ADMIN_SET_PERSON_STATUS = gql`
  mutation AdminSetPersonStatus($p: UUID!, $s: PersonStatus!) {
    adminSetPersonStatus(input: { p: $p, s: $s }) {
      result
    }
  }
`;

export type AdminSetPersonStatusResult = { adminSetPersonStatus: { result: string | null } };

// --- clubs (groups) ---
//
// The product name is "Groups"; the tables are `club`/`club_member`/
// `club_outing` because `group` is a reserved SQL keyword. Code identifiers
// follow the API (Club*), user-visible strings say "group".
//
// RLS the UI leans on (verified against the live API):
// - HIDDEN clubs are selectable only by their ACTIVE members. An INVITED
//   person sees their own clubMember row (clubId included) but the `club`
//   relation resolves null — which is why ClubMembershipRow.club is nullable
//   and GroupPage renders an accept/decline card from the bare row.
// - Member lists of PRIVATE/HIDDEN clubs are member-only; PENDING/INVITED/
//   BANNED rows are visible only to that person and to club admins.
// - Join flows: OPEN inserts {role: MEMBER, status: ACTIVE}; REQUEST_TO_JOIN
//   inserts {role: MEMBER, status: PENDING}; INVITE_ONLY has no self-join —
//   admins insert {status: INVITED} and the invitee patches their own row to
//   {role: MEMBER, status: ACTIVE} to accept, or deletes it to decline.
// - Leaving = deleting your own row (the owner cannot leave). Admins update
//   the club, only the owner deletes it.

export type ClubSummary = {
  id: string;
  name: string;
  visibility: ClubVisibility;
  joinPolicy: ClubJoinPolicy;
  activities: Activity[] | null;
  avatarKey: string | null;
};

// clubMember has no security-definer displayName column (unlike
// outingPerson); names come via the `person` relation, which resolves
// publicly since API migration 0012.
export type ClubMemberNode = {
  id: string;
  personId: string;
  role: ClubMemberRole;
  status: ClubMemberStatus;
  createdAt: string;
  person: { id: string; displayName: string | null; avatarKey: string | null } | null;
};

export const CLUB_DETAIL = gql`
  query ClubDetail($id: UUID!) {
    club(id: $id) {
      id
      name
      description
      visibility
      joinPolicy
      activities
      avatarKey
      createdBy
      createdAt
      updatedAt
      clubMembersByClubId(first: 200, orderBy: [CREATED_AT_ASC]) {
        totalCount
        nodes {
          id
          personId
          role
          status
          createdAt
          person {
            id
            displayName
            avatarKey
          }
        }
      }
      clubOutingsByClubId(first: 20, orderBy: [CREATED_AT_DESC]) {
        totalCount
        nodes {
          id
          outing {
            ...OutingCardFields
          }
        }
      }
      # Count only, for the tab badge — the events tab UI belongs to the
      # events feature.
      eventsByClubId(first: 0) {
        totalCount
      }
    }
  }
  ${OUTING_CARD_FIELDS}
`;

export type ClubDetailResult = {
  club:
    | (ClubSummary & {
        description: string | null;
        createdBy: string | null;
        createdAt: string;
        updatedAt: string;
        clubMembersByClubId: { totalCount: number; nodes: ClubMemberNode[] };
        // `outing` is null when the share points at an outing the viewer's
        // visibility doesn't cover (e.g. followers-only) — filter client-side.
        clubOutingsByClubId: { totalCount: number; nodes: { id: string; outing: OutingCardData | null }[] };
        eventsByClubId: { totalCount: number };
      })
    | null;
};

// One document serves the viewer's whole membership picture: the group page
// picks its club's row client-side (clubId is filterable too — it leads the
// (club_id, person_id) unique index — but a single personId-keyed query is
// shared with the outing share control and stays cached across pages).
export const MY_CLUB_MEMBERSHIPS = gql`
  query MyClubMemberships($personId: UUID!) {
    clubMembers(filter: { personId: { equalTo: $personId } }, first: 100) {
      nodes {
        id
        clubId
        role
        status
        club {
          id
          name
          visibility
          joinPolicy
          activities
          avatarKey
        }
      }
    }
  }
`;

export type ClubMembershipRow = {
  id: string;
  clubId: string;
  role: ClubMemberRole;
  status: ClubMemberStatus;
  // Null when the club is HIDDEN and the viewer isn't an ACTIVE member yet
  // (the INVITED state) — the row is visible, the club is not.
  club: ClubSummary | null;
};

export type MyClubMembershipsResult = { clubMembers: { nodes: ClubMembershipRow[] } };

// Same split as createOuting: a deferred DB constraint requires the owner
// club_member row to exist at commit, so it must be nested inside the same
// mutation via clubMembersByClubId { create: [...] }. Nested scalar enums
// (role/status) are name-mapped and stay UPPERCASE; the `activities` enum
// ARRAY must go through activitiesToWire() (see the TODO(pdbq) above).
export const CREATE_CLUB = gql`
  mutation CreateClub($input: ClubCreateInput!) {
    createClub(input: $input) {
      club {
        id
      }
    }
  }
`;

export type CreateClubResult = { createClub: { club: { id: string } } };

// Admin-only via RLS; a non-admin gets "no row matched" back.
export const UPDATE_CLUB = gql`
  mutation UpdateClub($id: UUID!, $patch: ClubUpdateInput!) {
    updateClub(id: $id, patch: $patch) {
      club {
        id
      }
    }
  }
`;

export type UpdateClubResult = { updateClub: { club: { id: string } | null } | null };

// Owner-only via RLS (admins may update, only the owner deletes).
export const DELETE_CLUB = gql`
  mutation DeleteClub($id: UUID!) {
    deleteClub(id: $id) {
      club {
        id
      }
    }
  }
`;

// One parameterized insert covers all three membership writes: self-join
// (MEMBER/ACTIVE on OPEN clubs), join request (MEMBER/PENDING on
// REQUEST_TO_JOIN clubs) and admin invite (MEMBER/INVITED). RLS decides
// which combination the caller may insert.
export const CREATE_CLUB_MEMBER = gql`
  mutation CreateClubMember($clubId: UUID!, $personId: UUID!, $role: ClubMemberRole!, $status: ClubMemberStatus!) {
    createClubMember(input: { clubId: $clubId, personId: $personId, role: $role, status: $status }) {
      clubMember {
        id
      }
    }
  }
`;

// Membership patches: accept an invite ({role: MEMBER, status: ACTIVE} on
// your own row), approve a request (status ACTIVE), ban (status BANNED),
// promote/demote (role ADMIN/MEMBER — never the owner row).
export const UPDATE_CLUB_MEMBER = gql`
  mutation UpdateClubMember($id: UUID!, $patch: ClubMemberUpdateInput!) {
    updateClubMember(id: $id, patch: $patch) {
      clubMember {
        id
        role
        status
      }
    }
  }
`;

// Leave / withdraw a request / decline an invite (own row), or admin
// kick / cancel-invite / unban (someone else's row).
export const DELETE_CLUB_MEMBER = gql`
  mutation DeleteClubMember($id: UUID!) {
    deleteClubMember(id: $id) {
      clubMember {
        id
      }
    }
  }
`;

// Keyed on the (club_id, outing_id) unique constraint, same shape as
// FOLLOW_PERSON: sharing is idempotent and unsharing needs no row-id lookup.
export const SHARE_OUTING_TO_CLUB = gql`
  mutation ShareOutingToClub($clubId: UUID!, $outingId: UUID!) {
    upsertClubOutingByClubIdAndOutingId(input: { clubId: $clubId, outingId: $outingId }) {
      clubOuting {
        id
      }
    }
  }
`;

export const UNSHARE_OUTING_FROM_CLUB = gql`
  mutation UnshareOutingFromClub($clubId: UUID!, $outingId: UUID!) {
    deleteClubOutingByClubIdAndOutingId(clubId: $clubId, outingId: $outingId) {
      clubOuting {
        id
      }
    }
  }
`;

// Which groups an outing is already shared with — drives the share control
// on the outing page. outingId leads an index, so the filter member exists;
// `club` is null for shares into clubs the viewer can't see.
export const OUTING_CLUB_SHARES = gql`
  query OutingClubShares($outingId: UUID!) {
    clubOutings(filter: { outingId: { equalTo: $outingId } }, first: 50) {
      nodes {
        id
        clubId
        club {
          id
          name
        }
      }
    }
  }
`;

export type OutingClubSharesResult = {
  clubOutings: { nodes: { id: string; clubId: string; club: { id: string; name: string } | null }[] };
};

// --- events ---
//
// Planned meetups, standalone or belonging to a club ("group" in the UI).
// RLS the UI leans on (verified against the live API):
// - Visibility: PUBLIC events are readable by everyone; MEMBERS events by the
//   club's ACTIVE members; PRIVATE events by participants and the creator. A
//   person INVITED to an event they can't see still sees their own
//   eventParticipant row (same shape as the hidden-club invite), so EventPage
//   renders an accept/decline card from the bare row.
// - Creating an event is ONE plain createEvent mutation: a DB trigger inserts
//   the creator as ORGANIZER/CONFIRMED — do NOT nest a participant create.
//   `createdBy` must be sent explicitly (RLS checks it against the JWT claim).
//   eventRoute links are follow-up mutations, like outing routes.
// - RSVP: OPEN inserts {ATTENDEE, CONFIRMED}; REQUEST_TO_JOIN inserts
//   {ATTENDEE, PENDING}; MEMBERS_ONLY inserts {ATTENDEE, CONFIRMED} (the
//   server verifies club membership); INVITE_ONLY/CLOSED have no self-join —
//   organizers insert {status: INVITED} and the invitee patches their own row
//   to CONFIRMED or DECLINED. Confirmed<->declined flips on your own row are
//   allowed; a PENDING row is approved by an organizer (patch to CONFIRMED)
//   or withdrawn by deleting your own row.
// - maxParticipants is advisory: the UI shows "n / cap going" and a full
//   hint, but neither the UI nor the server blocks joining a full event.

export type EventParticipantNode = {
  id: string;
  personId: string;
  role: EventParticipantRole;
  status: EventParticipantStatus;
  person: { id: string; displayName: string | null; avatarKey: string | null } | null;
};

export type EventRouteNode = {
  id: string;
  sort: number;
  route: RouteRef & { mainGeom: Geometry | null };
};

export const EVENT_DETAIL = gql`
  query EventDetail($id: UUID!) {
    event(id: $id) {
      id
      title
      description
      activities
      clubId
      placeId
      location
      startsAt
      endsAt
      joinPolicy
      visibility
      recurrence
      recurrenceUntil
      maxParticipants
      showOnMap
      createdBy
      createdAt
      updatedAt
      club {
        id
        name
      }
      place {
        id
        name
        type
        geom
      }
      # PENDING/INVITED/DECLINED rows are RLS-filtered to the person themself
      # and to organizers, so this list is already viewer-appropriate.
      eventParticipantsByEventId(first: 200, orderBy: [CREATED_AT_ASC]) {
        totalCount
        nodes {
          id
          personId
          role
          status
          person {
            id
            displayName
            avatarKey
          }
        }
      }
      eventRoutesByEventId(orderBy: [SORT_ASC]) {
        nodes {
          id
          sort
          route {
            id
            name
            i18n
            activities
            durationMin
            mainGeom(toleranceM: 15)
          }
        }
      }
    }
  }
`;

export type EventDetailResult = {
  event: {
    id: string;
    title: string;
    description: string | null;
    activities: Activity[] | null;
    clubId: string | null;
    placeId: string | null;
    location: GeoPoint | null;
    startsAt: string;
    endsAt: string | null;
    joinPolicy: EventJoinPolicy;
    visibility: EventVisibility;
    recurrence: EventRecurrence;
    recurrenceUntil: string | null;
    maxParticipants: number | null;
    showOnMap: boolean;
    createdBy: string | null;
    createdAt: string;
    updatedAt: string;
    club: { id: string; name: string } | null;
    place: { id: string; name: string; type: PlaceType; geom: GeoPoint } | null;
    eventParticipantsByEventId: { totalCount: number; nodes: EventParticipantNode[] };
    eventRoutesByEventId: { nodes: EventRouteNode[] };
  } | null;
};

// The group page's events tab: upcoming events of one club, soonest first.
// Both clubId and startsAt lead indexes, so the filter members and
// STARTS_AT_ASC exist.
export const UPCOMING_EVENTS_FOR_CLUB = gql`
  query UpcomingEventsForClub($clubId: UUID!, $from: Datetime!) {
    events(
      filter: { clubId: { equalTo: $clubId }, startsAt: { greaterThanOrEqualTo: $from } }
      orderBy: [STARTS_AT_ASC]
      first: 50
    ) {
      totalCount
      nodes {
        id
        title
        activities
        startsAt
        endsAt
        joinPolicy
        visibility
        recurrence
      }
    }
  }
`;

export type UpcomingClubEvent = {
  id: string;
  title: string;
  activities: Activity[] | null;
  startsAt: string;
  endsAt: string | null;
  joinPolicy: EventJoinPolicy;
  visibility: EventVisibility;
  recurrence: EventRecurrence;
};

export type UpcomingEventsForClubResult = { events: { totalCount: number; nodes: UpcomingClubEvent[] } };

// The viewer's own participation rows. `event` is null when the event itself
// is out of visibility (e.g. INVITED to a MEMBERS event of a club you're not
// in) — the row alone still carries enough to accept or decline.
export const MY_EVENT_PARTICIPATIONS = gql`
  query MyEventParticipations($personId: UUID!) {
    eventParticipants(filter: { personId: { equalTo: $personId } }, first: 100) {
      nodes {
        id
        eventId
        role
        status
        event {
          id
          title
          startsAt
          endsAt
          clubId
        }
      }
    }
  }
`;

export type EventParticipationRow = {
  id: string;
  eventId: string;
  role: EventParticipantRole;
  status: EventParticipantStatus;
  event: { id: string; title: string; startsAt: string; endsAt: string | null; clubId: string | null } | null;
};

export type MyEventParticipationsResult = { eventParticipants: { nodes: EventParticipationRow[] } };

// One plain mutation — the organizer participant row comes from a DB trigger,
// so nothing is nested here (nesting a participant create would collide with
// the trigger row). The `activities` enum ARRAY must go through
// activitiesToWire() (see the TODO(pdbq) above); `location` takes the GeoJSON
// object form, like createPlace.
export const CREATE_EVENT = gql`
  mutation CreateEvent($input: EventCreateInput!) {
    createEvent(input: $input) {
      event {
        id
      }
    }
  }
`;

export type CreateEventResult = { createEvent: { event: { id: string } } };

// Organizer/creator/club-admin only via RLS; anyone else gets "no row
// matched" back (updateEvent resolves null).
export const UPDATE_EVENT = gql`
  mutation UpdateEvent($id: UUID!, $patch: EventUpdateInput!) {
    updateEvent(id: $id, patch: $patch) {
      event {
        id
      }
    }
  }
`;

export type UpdateEventResult = { updateEvent: { event: { id: string } | null } | null };

export const DELETE_EVENT = gql`
  mutation DeleteEvent($id: UUID!) {
    deleteEvent(id: $id) {
      event {
        id
      }
    }
  }
`;

// One parameterized insert covers every participant write: self-join
// (ATTENDEE/CONFIRMED on OPEN and MEMBERS_ONLY events), join request
// (ATTENDEE/PENDING) and organizer invite (ATTENDEE/INVITED). RLS decides
// which combination the caller may insert.
export const CREATE_EVENT_PARTICIPANT = gql`
  mutation CreateEventParticipant($eventId: UUID!, $personId: UUID!, $role: EventParticipantRole!, $status: EventParticipantStatus!) {
    createEventParticipant(input: { eventId: $eventId, personId: $personId, role: $role, status: $status }) {
      eventParticipant {
        id
      }
    }
  }
`;

// Status patches: accept/decline an invite or flip going/can't-go (own row),
// approve a request (organizer, status CONFIRMED).
export const UPDATE_EVENT_PARTICIPANT = gql`
  mutation UpdateEventParticipant($id: UUID!, $patch: EventParticipantUpdateInput!) {
    updateEventParticipant(id: $id, patch: $patch) {
      eventParticipant {
        id
        status
      }
    }
  }
`;

// Withdraw a request / leave (own row), or organizer decline-request /
// cancel-invite / remove-participant (someone else's row).
export const DELETE_EVENT_PARTICIPANT = gql`
  mutation DeleteEventParticipant($id: UUID!) {
    deleteEventParticipant(id: $id) {
      eventParticipant {
        id
      }
    }
  }
`;

export const CREATE_EVENT_ROUTE = gql`
  mutation CreateEventRoute($eventId: UUID!, $routeId: UUID!, $sort: Int!) {
    createEventRoute(input: { eventId: $eventId, routeId: $routeId, sort: $sort }) {
      eventRoute {
        id
      }
    }
  }
`;

// Update/delete by the (event_id, route_id) natural key so the edit form's
// diff needs no row-id bookkeeping — same shape as the outing_route trio.
export const UPDATE_EVENT_ROUTE = gql`
  mutation UpdateEventRoute($eventId: UUID!, $routeId: UUID!, $sort: Int!) {
    updateEventRouteByEventIdAndRouteId(eventId: $eventId, routeId: $routeId, patch: { sort: $sort }) {
      eventRoute {
        id
      }
    }
  }
`;

export const DELETE_EVENT_ROUTE = gql`
  mutation DeleteEventRoute($eventId: UUID!, $routeId: UUID!) {
    deleteEventRouteByEventIdAndRouteId(eventId: $eventId, routeId: $routeId) {
      eventRoute {
        id
      }
    }
  }
`;

// --- discover ---
//
// Browsable, offset-paged lists of groups and events for the Discover page.
// RLS already trims HIDDEN clubs and members-only/private events for the
// viewer, so no visibility predicate appears in these filters.

export type DiscoverClubNode = ClubSummary & {
  description: string | null;
  // Active members only — status leads an index, so the nested filter is
  // valid and cheap.
  clubMembersByClubId: { totalCount: number };
};

export const DISCOVER_CLUBS = gql`
  query DiscoverClubs($filter: ClubFilter, $first: Int!, $offset: Int!) {
    clubs(filter: $filter, orderBy: [CREATED_AT_DESC], first: $first, offset: $offset) {
      totalCount
      nodes {
        id
        name
        description
        visibility
        joinPolicy
        activities
        avatarKey
        clubMembersByClubId(filter: { status: { equalTo: ACTIVE } }) {
          totalCount
        }
      }
    }
  }
`;

export type DiscoverClubsResult = { clubs: { totalCount: number; nodes: DiscoverClubNode[] } };

export type DiscoverEventNode = {
  id: string;
  title: string;
  activities: Activity[] | null;
  clubId: string | null;
  startsAt: string;
  endsAt: string | null;
  joinPolicy: EventJoinPolicy;
  visibility: EventVisibility;
  club: { id: string; name: string } | null;
};

// Deliberately cheap cards: no participant counts (a totalCount subquery per
// row would dominate the request cost for a browse list).
export const DISCOVER_EVENTS = gql`
  query DiscoverEvents($filter: EventFilter, $first: Int!, $offset: Int!) {
    events(filter: $filter, orderBy: [STARTS_AT_ASC], first: $first, offset: $offset) {
      totalCount
      nodes {
        id
        title
        activities
        clubId
        startsAt
        endsAt
        joinPolicy
        visibility
        club {
          id
          name
        }
      }
    }
  }
`;

export type DiscoverEventsResult = { events: { totalCount: number; nodes: DiscoverEventNode[] } };

// --- calendar ---

// Slim outing rows for calendar chips. One document serves both calendar
// sources via $filter: the viewer's own outings (any role, so joined outings
// count) and followees' outings (role OWNER, personId in followees — same
// shape as the Following feed). RLS trims what the viewer may not see; note
// followees' PLANNED outings never come back (follower select requires
// status = completed), only the viewer's own planned ones do.
export const CALENDAR_OUTINGS = gql`
  query CalendarOutings($filter: OutingFilter!, $first: Int!) {
    outings(filter: $filter, orderBy: [DATE_ASC], first: $first) {
      nodes {
        id
        title
        date
        activity
        status
        outingPeopleByOutingId {
          nodes {
            role
            personId
            displayName
          }
        }
      }
    }
  }
`;

export type CalendarOutingNode = {
  id: string;
  title: string;
  date: string;
  activity: Activity;
  status: OutingStatus;
  outingPeopleByOutingId: { nodes: { role: "OWNER" | "PARTICIPANT"; personId: string; displayName: string | null }[] };
};

export type CalendarOutingsResult = { outings: { nodes: CalendarOutingNode[] } };

// Events of the viewer's clubs inside the visible window. Both clubId and
// startsAt lead indexes, so the filter members exist; endsAt does not, so the
// window keys on start time only (multi-day events render from their start).
export const CALENDAR_CLUB_EVENTS = gql`
  query CalendarClubEvents($clubIds: [UUID!]!, $from: Datetime!, $to: Datetime!) {
    events(
      filter: { clubId: { in: $clubIds }, startsAt: { greaterThanOrEqualTo: $from, lessThanOrEqualTo: $to } }
      orderBy: [STARTS_AT_ASC]
      first: 200
    ) {
      nodes {
        id
        title
        startsAt
        endsAt
        activities
        clubId
        club {
          id
          name
        }
      }
    }
  }
`;

export type CalendarEventNode = {
  id: string;
  title: string;
  startsAt: string;
  endsAt: string | null;
  activities: Activity[] | null;
  clubId: string | null;
  club: { id: string; name: string } | null;
};

export type CalendarClubEventsResult = { events: { nodes: CalendarEventNode[] } };

// --- nearby (KNN-ordered SQL functions, capped at 50 server-side) ---

export const NEARBY = gql`
  query Nearby($lon: Float!, $lat: Float!, $maxN: Int!) {
    placesNearby(lon: $lon, lat: $lat, maxN: $maxN) {
      id
      name
      i18n
      type
      elevation
      geom
    }
    routesNearby(lon: $lon, lat: $lat, maxN: $maxN) {
      id
      name
      i18n
      activities
      startPoint
    }
  }
`;

export type NearbyResult = {
  placesNearby: PlacePin[];
  routesNearby: { id: string; name: string; i18n?: EntityI18n; activities: Activity[]; startPoint: GeoPoint | null }[];
};
